/**
 * Admin · access & MFA (GET/POST /admin/team, POST /admin/team/{id}/mfa/reset,
 * POST /admin/mfa/setup + /admin/mfa/verify, PUT /admin/policy).
 * Provision admins, reset MFA (owner), enrol your own TOTP, enforce the require-MFA policy.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminRole } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Alert, Info } from "../../components/icons.js";
import { Modal, Switch, apiErrorText } from "./adminUi.js";

interface AdminUser {
  userId: string;
  name: string;
  email: string;
  adminRole: string | null;
  mfaEnabled: boolean;
  createdAt: string;
}

interface TeamResponse {
  admins: AdminUser[];
  requireMfa: boolean;
}

interface ProvisionResponse {
  admin: AdminUser;
  invited: boolean;
}

interface MfaSetupResponse {
  otpauthUri: string;
  secret: string;
}

/**
 * Three roles, in plain language. This copy IS the definition the owner signed off, and it is the
 * last thing read before someone's access is decided — so it states what the role can CHANGE, not
 * which screens happen to render. Keep it in step with ADMIN_CAPS (packages/shared/src/roles.ts).
 */
const ROLE_OPTIONS: { value: AdminRole; label: string; help: string }[] = [
  {
    value: "owner",
    label: "Owner",
    help: "Full control: money, admin accounts, and access into client organisations.",
  },
  {
    value: "admin",
    label: "Admin",
    help: "Client organisations and the sourcing desk. No money settings, platform config or admin accounts.",
  },
  {
    value: "analyst",
    label: "Analyst",
    help: "Read-only view of the sourcing desk. Changes nothing.",
  },
];

export function AdminTeam(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-team"],
    queryFn: () => api.get<TeamResponse>("/admin/team"),
  });
  const admins = data?.admins ?? [];
  const self = admins.find((a) => a.userId === me?.userId) ?? null;
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ["admin-team"] });

  // --- require-MFA policy (owner) ---
  const policy = useMutation({
    mutationFn: (requireMfa: boolean) => api.put<unknown>("/admin/policy", { requireMfa }),
    onSuccess: invalidate,
  });

  // --- provision a platform admin (owner; a secure set-password invite is emailed, no temp password) ---
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  // Defaults to the least-privileged role: granting more is a deliberate act, never the path of
  // least resistance.
  const [newRole, setNewRole] = useState<AdminRole>("analyst");
  const provision = useMutation({
    mutationFn: () =>
      api.post<ProvisionResponse>("/admin/team", {
        name: newName.trim(),
        email: newEmail.trim(),
        role: newRole,
      }),
    onSuccess: () => {
      setNewName("");
      setNewEmail("");
      invalidate();
    },
  });

  // --- MFA reset (owner, confirm modal) ---
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const resetMfa = useMutation({
    mutationFn: (userId: string) => api.post<unknown>(`/admin/team/${userId}/mfa/reset`),
    onSuccess: () => {
      setResetTarget(null);
      invalidate();
    },
  });

  // --- deprovision an admin (owner, confirm modal). Fully removes platform access and revokes
  //     every live session server-side; the last-owner guard lives in the API. ---
  const [removeTarget, setRemoveTarget] = useState<AdminUser | null>(null);
  const deprovision = useMutation({
    mutationFn: (userId: string) => api.post<unknown>(`/admin/team/${userId}/access`, { role: null }),
    onSuccess: () => {
      setRemoveTarget(null);
      invalidate();
    },
  });

  // --- self MFA enrolment (setup → secret shown once → verify code) ---
  const [enrolment, setEnrolment] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const setup = useMutation({
    mutationFn: () => api.post<MfaSetupResponse>("/admin/mfa/setup"),
    onSuccess: (res) => setEnrolment(res),
  });
  const verify = useMutation({
    mutationFn: () => api.post<{ mfaEnabled: boolean }>("/admin/mfa/verify", { code }),
    onSuccess: () => {
      setEnrolment(null);
      setCode("");
      invalidate();
    },
  });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Access & MFA</h1>
          <div className="sub">Provision admins, reset credentials, and enforce MFA · owner-gated actions</div>
        </div>
        <span className="pill n">Security</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading platform team…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load the platform team. Please retry in a moment.</span>
        </div>
      )}

      {data && (
        <>
          <div className="card pad" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 650 }}>Require MFA for all admins</div>
              <div className="mut sm">
                Any enrolled second factor — authenticator app (TOTP) or email code — satisfies this ·
                applies to every admin
              </div>
            </div>
            {policy.isError && (
              <span className="sm" style={{ color: "var(--crit)" }}>
                {apiErrorText(policy.error, "Couldn't update the policy.")}
              </span>
            )}
            <Switch
              on={data.requireMfa}
              disabled={!isOwner || policy.isPending}
              onToggle={() => policy.mutate(!data.requireMfa)}
            />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Platform team</h3>
              <span className="mut sm">Provision · enable / reset MFA · owner-only</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>MFA</th>
                    <th>Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => (
                    <tr key={a.userId}>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {a.name}
                          {a.userId === me?.userId && (
                            <span className="pill n" style={{ marginLeft: 8 }}>
                              you
                            </span>
                          )}
                        </div>
                        <div className="mut sm">{a.email}</div>
                      </td>
                      {/* Label from ROLE_OPTIONS, not a prettified raw value — a role string we
                          don't recognise reaches no surface at all, so it must not read as a
                          working role in the one table that documents who can do what. */}
                      <td>{ROLE_OPTIONS.find((r) => r.value === a.adminRole)?.label ?? "No access"}</td>
                      <td>
                        {a.mfaEnabled ? (
                          <span className="pill v">Enabled</span>
                        ) : (
                          <span className="pill r">Not enabled</span>
                        )}
                      </td>
                      <td className="mut sm">{new Date(a.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className="btn sm ghost"
                            disabled={!isOwner || !a.mfaEnabled}
                            onClick={() => setResetTarget(a)}
                          >
                            Reset MFA
                          </button>
                          <button
                            className="btn sm ghost"
                            // Can't deprovision yourself — avoids an accidental self-lockout; the
                            // API also refuses to remove the last owner.
                            disabled={!isOwner || a.userId === me?.userId}
                            onClick={() => setRemoveTarget(a)}
                          >
                            Deprovision
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Grant admin access</h3>
                <span className="tag">Owner</span>
              </div>
              <div className="card-b">
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Name</label>
                  <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Email</label>
                  <input
                    className="input"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="name@talenthop.ai"
                  />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Role</label>
                  <select value={newRole} onChange={(e) => setNewRole(e.target.value as AdminRole)}>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <div className="mut sm" style={{ marginTop: 6 }}>
                    {ROLE_OPTIONS.find((r) => r.value === newRole)?.help}
                  </div>
                </div>
                {provision.isError && (
                  <div className="banner crit" style={{ marginBottom: 12 }}>
                    <span>{apiErrorText(provision.error, "Couldn't provision the admin. Please retry.")}</span>
                  </div>
                )}
                {provision.data && (
                  <div className="banner info" style={{ marginBottom: 12 }}>
                    <Info />
                    <div>
                      <b>{provision.data.admin.email}</b> provisioned. An invite email with a secure
                      set-password link has been sent — no temporary password is created or shared here.
                    </div>
                  </div>
                )}
                <button
                  className="btn primary"
                  disabled={!isOwner || newName.trim().length === 0 || !newEmail.includes("@") || provision.isPending}
                  onClick={() => provision.mutate()}
                >
                  {provision.isPending ? "Provisioning…" : "Grant admin access"}
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Your MFA</h3>
                {self?.mfaEnabled ? <span className="pill v">Enabled</span> : <span className="pill r">Not enrolled</span>}
              </div>
              <div className="card-b">
                {self?.mfaEnabled && !enrolment && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    TOTP is active on your account. Sign-in requires a 6-digit code. Re-enrol only after an owner
                    resets your MFA.
                  </p>
                )}
                {!self?.mfaEnabled && !enrolment && (
                  <>
                    <p className="mut sm" style={{ marginTop: 0 }}>
                      Enrol a TOTP authenticator (Google Authenticator, 1Password…). You'll be shown the secret
                      exactly once.
                    </p>
                    {setup.isError && (
                      <div className="banner crit" style={{ marginBottom: 12 }}>
                        <span>{apiErrorText(setup.error, "Couldn't start enrolment. Please retry.")}</span>
                      </div>
                    )}
                    <button className="btn primary" disabled={setup.isPending} onClick={() => setup.mutate()}>
                      {setup.isPending ? "Generating…" : "Set up MFA"}
                    </button>
                  </>
                )}
                {enrolment && (
                  <>
                    <div className="banner warn" style={{ marginBottom: 12 }}>
                      <Alert />
                      <div>
                        Add this secret to your authenticator <b>now</b> — it is shown once and never again.
                      </div>
                    </div>
                    <div className="krow">
                      <span className="k">Secret (base32)</span>
                      <span className="v" style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>
                        {enrolment.secret}
                      </span>
                    </div>
                    <div className="krow" style={{ marginBottom: 12 }}>
                      <span className="k">otpauth URI</span>
                      <span
                        className="v mut sm"
                        style={{ fontFamily: "var(--mono)", fontSize: 12, wordBreak: "break-all", maxWidth: 260 }}
                      >
                        {enrolment.otpauthUri}
                      </span>
                    </div>
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label>6-digit code from your app</label>
                      <input
                        className="input"
                        inputMode="numeric"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                      />
                    </div>
                    {verify.isError && (
                      <div className="banner crit" style={{ marginBottom: 12 }}>
                        <span>{apiErrorText(verify.error, "Code didn't verify — check your app clock and retry.")}</span>
                      </div>
                    )}
                    <button
                      className="btn primary"
                      disabled={code.length !== 6 || verify.isPending}
                      onClick={() => verify.mutate()}
                    >
                      {verify.isPending ? "Verifying…" : "Verify & enable"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>MFA secrets are encrypted at rest</b> and never rendered after enrolment. Grants, resets
        and policy changes are owner-only and written to the audit log.
      </div>

      {resetTarget && (
        <Modal title={`Reset MFA for ${resetTarget.name}?`} onClose={() => setResetTarget(null)}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            Their TOTP secret is destroyed and every existing session is revoked (token version bump). They must
            re-enrol at next login. This is logged.
          </p>
          {resetMfa.isError && (
            <div className="banner crit" style={{ marginBottom: 12 }}>
              <span>{apiErrorText(resetMfa.error, "Couldn't reset MFA. Please retry.")}</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={() => setResetTarget(null)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={resetMfa.isPending}
              onClick={() => resetMfa.mutate(resetTarget.userId)}
            >
              {resetMfa.isPending ? "Resetting…" : "Reset MFA & revoke sessions"}
            </button>
          </div>
        </Modal>
      )}

      {removeTarget && (
        <Modal title={`Deprovision ${removeTarget.name}?`} onClose={() => setRemoveTarget(null)}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            Platform access is removed and every existing session is revoked immediately (token
            version bump). They can no longer sign in to the console. This is logged. The last
            remaining owner cannot be removed.
          </p>
          {deprovision.isError && (
            <div className="banner crit" style={{ marginBottom: 12 }}>
              <span>{apiErrorText(deprovision.error, "Couldn't deprovision this admin. Please retry.")}</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={() => setRemoveTarget(null)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={deprovision.isPending}
              onClick={() => deprovision.mutate(removeTarget.userId)}
            >
              {deprovision.isPending ? "Removing…" : "Deprovision & revoke sessions"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
